import api from '@services/api';

/**
 * Utility functions for managing resource metadata
 */
export class MetadataManager {
  /**
   * Synchronize metadata for a resource after create/update
   * @param {number} resourceId - The resource ID
   * @param {Array} metadataArray - Array of metadata objects {metadata_key, value}
   * @param {Array} existingMetadata - Existing metadata to compare against
   */
  static async syncResourceMetadata(resourceId, metadataArray = [], existingMetadata = []) {
    if (!resourceId) {
      console.warn('Cannot sync metadata: resourceId is required');
      return;
    }

    try {
      // Convert arrays to maps for easier comparison
      const newMetadataMap = new Map();
      const allNewKeys = new Set();

      metadataArray.forEach(item => {
        if (item.metadata_key) {
          allNewKeys.add(item.metadata_key);
          // Only add to map if value is not empty
          if (item.value !== '' && item.value !== null && item.value !== undefined) {
            newMetadataMap.set(item.metadata_key, String(item.value));
          }
          // If value is empty, the key will be in allNewKeys but not in newMetadataMap
          // This signals that existing metadata for this key should be deleted
        }
      });

      const existingMetadataMap = new Map();
      existingMetadata.forEach(item => {
        if (item.metadata_key) {
          existingMetadataMap.set(item.metadata_key, item.value);
        }
      });

      const operations = [];

      // Find metadata to create or update
      for (const [key, value] of newMetadataMap.entries()) {
        if (existingMetadataMap.has(key)) {
          // Update if value changed
          if (existingMetadataMap.get(key) !== value) {
            operations.push({
              type: 'upsert',
              key,
              value,
              promise: api.metadata.upsertResourceMetadata(resourceId, key, value)
            });
          }
        } else {
          // Create new metadata
          operations.push({
            type: 'create',
            key,
            value,
            promise: api.metadata.createResourceMetadata({
              resource_id: resourceId,
              metadata_key: key,
              value
            })
          });
        }
      }

      // Find metadata to delete
      for (const [key] of existingMetadataMap.entries()) {
        // Delete if:
        // 1. Key is not in the new metadata (removed completely)
        // 2. Key is in allNewKeys but not in newMetadataMap (value was cleared)
        if (!allNewKeys.has(key) || (allNewKeys.has(key) && !newMetadataMap.has(key))) {
          operations.push({
            type: 'delete',
            key,
            promise: api.metadata.deleteResourceMetadata(resourceId, key)
          });
        }
      }

      // Execute all operations
      if (operations.length > 0) {
        const results = await Promise.allSettled(operations.map(op => op.promise));

        // Log any failures
        results.forEach((result, index) => {
          const operation = operations[index];
          if (result.status === 'rejected') {
            console.error(`Failed to ${operation.type} metadata ${operation.key}:`, result.reason);
          }
        });

        console.log(`Metadata sync completed: ${operations.length} operations performed`);
      }

      return operations.length;
    } catch (error) {
      console.error('Error syncing resource metadata:', error);
      throw error;
    }
  }

  /**
   * Get metadata definitions filtered by resource type
   * @param {boolean} isConsumable - Whether the resource is consumable
   * @param {boolean} showAdvanced - Whether to show all definitions
   * @returns {Promise<Array>} Array of metadata definitions
   */
  static async getFilteredDefinitions(isConsumable = false, showAdvanced = false) {
    try {
      const params = {
        page: 1,
        page_size: -1, // Get all definitions
      };

      if (!showAdvanced) {
        if (isConsumable) {
          params.applies_to_consumable = true;
        } else {
          params.applies_to_non_consumable = true;
        }
      }

      const response = await api.metadata.getDefinitions(params);
      return response.data.models || [];
    } catch (error) {
      console.error('Error fetching metadata definitions:', error);
      throw error;
    }
  }

  /**
   * Validate metadata values against their definitions
   * @param {Array} metadataArray - Array of metadata objects
   * @param {Array} definitions - Array of metadata definitions
   * @returns {Object} Validation result with errors
   */
  static validateMetadata(metadataArray = [], definitions = []) {
    const errors = {};
    const definitionsMap = new Map();

    // Create map of definitions for quick lookup
    definitions.forEach(def => {
      definitionsMap.set(def.key_name, def);
    });

    // Check required fields
    definitions.forEach(def => {
      if (def.is_required) {
        const metadataItem = metadataArray.find(item => item.metadata_key === def.key_name);
        if (!metadataItem || !metadataItem.value || metadataItem.value.trim() === '') {
          errors[def.key_name] = `${def.label} is required`;
        }
      }
    });

    // Validate data types and formats
    metadataArray.forEach(item => {
      const definition = definitionsMap.get(item.metadata_key);
      if (definition && item.value) {
        const value = item.value.trim();

        switch (definition.data_type) {
          case 'integer':
            if (!/^-?\d+$/.test(value)) {
              errors[item.metadata_key] = `${definition.label} must be a whole number`;
            }
            break;

          case 'decimal':
            if (!/^-?\d*\.?\d+$/.test(value)) {
              errors[item.metadata_key] = `${definition.label} must be a number`;
            }
            break;

          case 'date':
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
              errors[item.metadata_key] = `${definition.label} must be a valid date`;
            }
            break;

          case 'boolean':
            if (!['true', 'false'].includes(value.toLowerCase())) {
              errors[item.metadata_key] = `${definition.label} must be true or false`;
            }
            break;

          default:
            // 'string' type and other types don't need additional validation
            break;
        }
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Format metadata value for display
   * @param {string} value - The raw metadata value
   * @param {Object} definition - The metadata definition
   * @returns {string} Formatted value
   */
  static formatValueForDisplay(value, definition) {
    if (!value || value === '') return '';

    switch (definition.data_type) {
      case 'boolean':
        if (value === 'true' || value === true) return 'Oui';
        if (value === 'false' || value === false) return 'Non';
        return value;

      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return value;
        }

      case 'decimal':
        try {
          return parseFloat(value).toLocaleString();
        } catch {
          return value;
        }

      case 'integer':
        try {
          return parseInt(value).toLocaleString();
        } catch {
          return value;
        }

      default:
        return value;
    }
  }
}

export default MetadataManager;
